import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useState, type ReactNode } from "react";

import { Add } from "@mui/icons-material";
import { Dialogue } from "src/reusable/Chat/types/chatTypes";
import { PageLayout } from "src/reusable/components/DashboardComponents";
import RecentChatCard from "./RecentChatCard";
import SearchBar from "src/views/datasets/components/SearchBar";
import { styled } from "@mui/material/styles";
import { useDebounce } from "src/helpers/helperFunctions";
import { useGetAllDialogues } from "../../AssistantServices";
import { useHistory } from "react-router-dom";
import { useChatStore } from "src/views/assistant/stores/ChatStore";

const LoadingBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(4),
}));

const ErrorBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  color: theme.palette.error.main,
  textAlign: "center",
}));

interface AllChatsProps {
  /**
   * Product whose chats this page shows. Defaults to the persisted
   * productConfig, but routes that mount AllChats directly (e.g.
   * /assistant/recents) should pass it explicitly.
   */
  productKey?: string;
  /** Base path for default chat URLs + "new chat". Defaults to productConfig. */
  basePath?: string;
  /**
   * Optional product-specific query params forwarded to /chat/dialogue
   * (e.g. {kind: "search"} for Parts AI dialogue-kind scoping, or
   * {location_id: "L123"} for equipment-AI). Used to scope history without
   * forking the shared list UI.
   */
  extraFilters?: Record<string, string>;
  /**
   * Optional header line rendered above the chat list (e.g. a "Chats for
   * <asset name>" caption when the list is scoped).
   */
  scopeLabel?: string;
  /**
   * Show the "New chat" button. Products whose basePath isn't a new-chat
   * surface (e.g. Equipment AI's dashboard) pass false. Defaults to true. (#1927)
   */
  canStartNewChat?: boolean;
  /**
   * Override the "New chat" action. When provided it replaces the default
   * basePath push — for products with a non-basePath new-chat target (e.g.
   * Equipment AI routing into a scoped asset chat). (#24194)
   */
  onStartNewChat?: () => void;
  /**
   * Suppress the internal "Chat history" title/subtitle/"New chat" button
   * block. Used when AllChats is embedded inside a page that supplies its
   * own heading above the list (e.g. the Parts AI home page).
   */
  hideHeader?: boolean;
  /**
   * Extra content rendered inside the same width-constrained Container as the
   * search bar and chat rows (e.g. a product-specific title + filter control),
   * so it lines up exactly with them instead of whatever width the embedding
   * page happens to use.
   */
  headerExtra?: ReactNode;
  /** Vertical gap between chat rows. Defaults to 3 (the standalone page look). */
  rowSpacing?: number;
  /**
   * Optional per-row badge (e.g. Parts AI's kind chip). Kept out of this
   * generic component — the product-specific caller supplies its own
   * renderer instead of AllChats importing product UI directly.
   */
  renderChatBadge?: (dialogue: Dialogue) => ReactNode;
}

const AllChats = ({
  productKey: productKeyProp,
  basePath: basePathProp,
  extraFilters,
  scopeLabel,
  canStartNewChat = true,
  onStartNewChat,
  hideHeader = false,
  headerExtra,
  rowSpacing = 3,
  renderChatBadge,
}: AllChatsProps = {}) => {
  const history = useHistory();
  const config = useChatStore((state) => state.productConfig);
  const productKey = productKeyProp ?? config.productKey;
  const basePath = basePathProp ?? config.basePath;
  // Resolve the builder by this page's product, not whichever product
  // registered last globally (prevents cross-product misrouting).
  const buildChatUrl = useChatStore(
    (state) => state.buildChatUrlRegistry[productKey],
  );
  const sanitizedBasePath = basePath.endsWith("/")
    ? basePath.slice(0, -1)
    : basePath;
  const [searchQuery, setSearchQuery] = useState("");

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const {
    data: dialogues,
    isLoading: isDialoguesLoading,
    isError: isDialoguesError,
    isSuccess: isDialoguesSuccess,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetAllDialogues(debouncedSearchQuery, productKey, extraFilters);

  // Flatten all pages of dialogues into a single array
  const allChats = dialogues?.pages?.flatMap((page) => page.data) || [];

  const navigateToChat = async (dialogue: Dialogue) => {
    // Use custom URL builder if provided, otherwise use default pattern
    const url = buildChatUrl
      ? await buildChatUrl(dialogue.id, dialogue.kind)
      : `${sanitizedBasePath}/chat/${dialogue.id}`;
    history.push(url);
  };

  const startNewChat = () => {
    if (onStartNewChat) {
      onStartNewChat();
      return;
    }
    history.push(sanitizedBasePath);
  };

  return (
    <PageLayout>
      {!hideHeader && (
        <Stack
          direction="row"
          gap={1}
          justifyContent="space-between"
          alignItems="flex-end"
          mb={4}
          mt={1.5}
        >
          <Box>
            <Typography variant="h1" fontWeight="bold" fontSize="1.5rem">
              Chat history
            </Typography>
            <Typography variant="body1">
              {scopeLabel ??
                "Quickly access and review your past chat histories, kept together for easy reference."}
            </Typography>
          </Box>

          {canStartNewChat && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={startNewChat}
              data-attr="assistant:btn:new-chat-from-history"
              sx={{
                flexShrink: 0,
              }}
            >
              New chat
            </Button>
          )}
        </Stack>
      )}

      <Container maxWidth="lg">
        {headerExtra && <Box sx={{ mb: 3 }}>{headerExtra}</Box>}
        <Box sx={{ mb: 3 }}>
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            placeholder="Search for chats"
            data-testid="aa-chat-search-bar"
          />
        </Box>
        {isDialoguesLoading && (
          <LoadingBox>
            <CircularProgress />
          </LoadingBox>
        )}
        {isDialoguesError && (
          <ErrorBox>We're having trouble fetching your chat history.</ErrorBox>
        )}
        {isDialoguesSuccess && (
          <Grid container spacing={rowSpacing}>
            {allChats.length > 0 ? (
              allChats.map((dialogue: Dialogue) => (
                <Grid item xs={12} md={12} lg={12} key={dialogue.id}>
                  <RecentChatCard
                    dialogue={dialogue}
                    onClick={navigateToChat}
                    type="all"
                    renderBadge={renderChatBadge}
                  />
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography color="text.secondary" align="center">
                  No chats available
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </Container>

      {isFetchingNextPage && (
        <LoadingBox>
          <CircularProgress size={24} />
        </LoadingBox>
      )}

      {hasNextPage && !isFetchingNextPage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => fetchNextPage()}
            data-attr="assistant:btn:load-more-chats"
          >
            Load more
          </Button>
        </Box>
      )}
    </PageLayout>
  );
};

export default AllChats;
